// src/context/LanguageContext.js
import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { translations } from '../constants/translations';

export const LanguageContext = createContext();

// Missing-key warnings are deduplicated for the whole session — one line per
// unique key instead of one per render.
const warnedMissingKeys = new Set();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Default to Turkish. Only honour a saved language if the user EXPLICITLY
    // picked one via the switcher (older builds auto-saved 'en', so we ignore
    // that unless the explicit flag is present).
    if (typeof window !== 'undefined') {
      const explicit = localStorage.getItem('languageExplicit');
      return explicit ? (localStorage.getItem('language') || 'tr') : 'tr';
    }
    return 'tr';
  });

  useEffect(() => {
    const explicit = localStorage.getItem('languageExplicit');
    const savedLanguage = explicit ? (localStorage.getItem('language') || 'tr') : 'tr';
    if (savedLanguage !== language) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const changeLanguage = useCallback((lang) => {
    if (translations[lang]) {
      // Mark that the user made an explicit choice so it survives reloads.
      try { localStorage.setItem('languageExplicit', 'true'); } catch (e) {}
      setLanguage(lang);
    } else {
      console.warn(`Language "${lang}" not found in translations`);
    }
  }, []);

  const t = useCallback((key, params = {}) => {
    if (!key || typeof key !== 'string') {
      return key || '';
    }

    // Check if translations object exists
    if (!translations || !translations[language]) {
      console.error(`Translations not loaded for language: ${language}`);
      return key;
    }

    const keys = key.split('.');
    let value = translations[language];

    // Navigate through the translation object
    for (const k of keys) {
      if (value && typeof value === 'object' && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        if (translations.en) {
          value = translations.en;
          for (const fallbackKey of keys) {
            if (value && typeof value === 'object' && value[fallbackKey] !== undefined) {
              value = value[fallbackKey];
            } else {
              // If still not found, return the key.
              // Warn ONCE per key per session — this line used to fire on every
              // render (~5,000 rows/day/branch in cloud logs), drowning real events.
              if (!warnedMissingKeys.has(key)) {
                warnedMissingKeys.add(key);
                console.warn(`Translation key not found: ${key} (language: ${language})`);
              }
              return key;
            }
          }
        } else {
          console.error('English translations not found');
          return key;
        }
        break;
      }
    }

    // Replace parameters in translation string
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }

    return typeof value === 'string' ? value : key;
  }, [language]);

  const value = useMemo(() => ({
    language,
    changeLanguage,
    t,
    setLanguage,
    translations: translations[language] || translations.en,
  }), [language, changeLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;

